// Tax HHOAT Report Submission Functions
import api from '../../../shared/utils/api';

/**
 * Tax HHOAT Report Submission Interface
 * Жил болон улирлын мэдээлэл аваад дараалсан API service-үүдийг дуудаж ажиллуулах
 */

// Tax report submission parameters
export interface TaxReportSubmissionParams {
  year: number;
  taxPeriod: number;
  entId?: string;
  accountantOid: string;
  formData?: any;
  formNo?: string; // Add formNo parameter
  taxTypeId?: number; // Add taxTypeId parameter
  branchId?: number; // Add branchId parameter
  isXreport?: number; // Add isXreport parameter
}

// API response interfaces
export interface TaxApiResponse {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
  details?: any;
}

// Tax report submission steps
export interface TaxSubmissionStep {
  step: number;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  message?: string;
  data?: any;
  error?: string;
}

/**
 * Tax HHOAT Report Submission Main Function
 * "Тайлан илгээх" товчийн үндсэн функц
 * 
 * @param params - Жил, улирал, entId, accountantOid
 * @param onProgress - Ахиц дэвшлийн callback функц
 * @returns Promise<TaxApiResponse>
 */
export const submitTaxHHOATReport = async (
  params: TaxReportSubmissionParams,
  onProgress?: (step: TaxSubmissionStep) => void
): Promise<TaxApiResponse> => {
  try {
    console.log('📤 Tax HHOAT Report submission эхэлж байна...', params);
    console.log('🔍 Submission parameters дэлгэрэнгүй:');
    console.log('  - year:', params.year);
    console.log('  - taxPeriod:', params.taxPeriod);
    console.log('  - entId:', params.entId);
    console.log('  - accountantOid:', params.accountantOid);
    console.log('  - formData:', params.formData);

    const steps: TaxSubmissionStep[] = [
      { step: 1, name: 'Form data хадгалах', status: 'pending' },
      { step: 2, name: 'Sheet data устгах', status: 'pending' },
      { step: 3, name: 'Sheet data хадгалах', status: 'pending' },
      { step: 4, name: 'Form data хадгалах', status: 'pending' },
      { step: 5, name: 'Report илгээх', status: 'pending' }
    ];

    let currentStepIndex = 0;
    let reportNo: string | null = null;
    let entId = params.entId;

    // Step 1: Form data хадгалах (TaxSaveFormData)
    console.log('📝 Step 1: Form data хадгалах...');
    steps[0].status = 'running';
    onProgress?.(steps[0]);

    console.log('🔍 Step 1 parameters дэлгэрэнгүй:');
    console.log('  - entId:', entId || '10913884');
    console.log('  - accountantOid:', params.accountantOid);
    console.log('  - formData:', params.formData);
    console.log('  - year:', params.year);
    console.log('  - period:', params.taxPeriod);

    try {
      const formDataResult = await saveFormData({
        entId: entId || '10913884', // Default entId from spreadsheet
        accountantOid: params.accountantOid,
        formData: params.formData,
        year: params.year,
        period: params.taxPeriod,
        taxTypeId: params.taxTypeId, // Pass taxTypeId from request
        formNo: params.formNo // Pass formNo from request
      });

      if (!formDataResult.success) {
        steps[0].status = 'failed';
        steps[0].error = formDataResult.error;
        onProgress?.(steps[0]);
        return {
          success: false,
          message: 'Form data хадгалахад алдаа гарлаа',
          error: formDataResult.error,
          details: formDataResult
        };
      }

      // reportNo-г авч авах
      reportNo = formDataResult.data?.reportNo;
      entId = formDataResult.data?.entId || entId;
      
      console.log('✅ Form data амжилттай хадгалагдлаа, reportNo:', reportNo);
      console.log('🔍 Step 1 result дэлгэрэнгүй:');
      console.log('  - reportNo:', reportNo);
      console.log('  - entId:', entId);
      console.log('  - formDataResult:', formDataResult);
      console.log('  - formDataResult.data:', formDataResult.data);
      console.log('  - formDataResult.data.reportNo:', formDataResult.data?.reportNo);
      
      // isXreport: 1 бол мэдээлэл байхгүй тул шууд report-submit дуудах
      if (formDataResult.data?.isXreport === 1) {
        console.log('⚠️ isXreport: 1 - мэдээлэл байхгүй, шууд report-submit дуудах');
        
        // Step 5: Report илгээх (TaxReportSubmit) - шууд дуудах
        console.log('📤 Step 5: Report илгээх (шууд)...');
        steps[4].status = 'running';
        onProgress?.(steps[4]);
        
        try {
          const submitResult = await submitReport({
            entId: entId,
            accountantOid: params.accountantOid,
            submitData: { reportNo: reportNo },
            taxTypeId: formDataResult.data?.taxTypeId,
            formNo: formDataResult.data?.formNo,
            branchId: formDataResult.data?.branchId,
            year: formDataResult.data?.year,
            period: formDataResult.data?.period,
            isXreport: formDataResult.data?.isXreport
          });
          
          if (!submitResult.success) {
            steps[4].status = 'failed';
            steps[4].error = submitResult.error;
            onProgress?.(steps[4]);
            return {
              success: false,
              message: 'Report илгээхэд алдаа гарлаа',
              error: submitResult.error,
              details: submitResult
            };
          }
          
          steps[4].status = 'completed';
          steps[4].message = 'Report амжилттай илгээгдлээ';
          steps[4].data = submitResult.data;
          onProgress?.(steps[4]);
          
          return {
            success: true,
            message: 'Tax HHOAT Report амжилттай илгээгдлээ (шууд)',
            data: {
              reportNo: reportNo,
              entId: entId,
              isXreport: 1,
              steps: steps
            }
          };
          
        } catch (error: any) {
          console.error('❌ Report илгээхэд алдаа:', error);
          steps[4].status = 'failed';
          steps[4].error = error.message;
          onProgress?.(steps[4]);
          return {
            success: false,
            message: 'Report илгээхэд алдаа гарлаа',
            error: error.message
          };
        }
      }
      
      steps[0].status = 'completed';
      steps[0].message = `Form data хадгалагдлаа, reportNo: ${reportNo}`;
      steps[0].data = formDataResult.data;
      onProgress?.(steps[0]);

    } catch (error: any) {
      console.error('❌ Form data хадгалахад алдаа:', error);
      steps[0].status = 'failed';
      steps[0].error = error.message;
      onProgress?.(steps[0]);
      return {
        success: false,
        message: 'Form data хадгалахад алдаа гарлаа',
        error: error.message
      };
    }

    // Step 2: Sheet data устгах (TaxDelAllSheetdata)
    console.log('🗑️ Step 2: Sheet data устгах...');
    steps[1].status = 'running';
    onProgress?.(steps[1]);

    console.log('🔍 Step 2 parameters дэлгэрэнгүй:');
    console.log('  - reportNo:', reportNo);
    console.log('  - sheetFormNo:', ['1070']);
    console.log('  - accountantOid:', params.accountantOid);
    console.log('  - entId:', entId);

    try {
      if (!reportNo) {
        throw new Error('ReportNo олдсонгүй');
      }

      const deleteResult = await deleteAllSheetData({
        reportNo: reportNo,
        sheetFormNo: ['1070'], // Зөв sheet form number ашиглах
        accountantOid: params.accountantOid,
        entId: entId
      });

      if (!deleteResult.success) {
        steps[1].status = 'failed';
        steps[1].error = deleteResult.error;
        onProgress?.(steps[1]);
        return {
          success: false,
          message: 'Sheet data устгахад алдаа гарлаа',
          error: deleteResult.error,
          details: deleteResult
        };
      }

      console.log('✅ Sheet data амжилттай устгагдлаа');
      console.log('🔍 Step 2 result дэлгэрэнгүй:');
      console.log('  - deleteResult:', deleteResult);
      console.log('  - deleteResult.data:', deleteResult.data);
      console.log('  - deleteResult.message:', deleteResult.message);
      console.log('  - successCount:', deleteResult.data?.successCount);
      console.log('  - totalSheets:', deleteResult.data?.totalSheets);
      
      steps[1].status = 'completed';
      steps[1].message = `Sheet data устгагдлаа: ${deleteResult.data?.successCount}/${deleteResult.data?.totalSheets}`;
      steps[1].data = deleteResult.data;
      onProgress?.(steps[1]);

    } catch (error: any) {
      console.error('❌ Sheet data устгахад алдаа:', error);
      steps[1].status = 'failed';
      steps[1].error = error.message;
      onProgress?.(steps[1]);
      return {
        success: false,
        message: 'Sheet data устгахад алдаа гарлаа',
        error: error.message
      };
    }

    // Step 3: Sheet data хадгалах (TaxSaveSheetData)
    console.log('💾 Step 3: Sheet data хадгалах...');
    steps[2].status = 'running';
    onProgress?.(steps[2]);

    console.log('🔍 Step 3 parameters дэлгэрэнгүй:');
    console.log('  - entId:', entId);
    console.log('  - sheetFormNo:', [1070]);
    console.log('  - sheetCode:', ['XM_11(1)_6']);
    console.log('  - accountantOid:', params.accountantOid);
    console.log('  - reportNo:', reportNo);
    console.log('  - year:', params.year);
    console.log('  - period:', params.taxPeriod);

    try {
      const saveSheetResult = await saveSheetData({
        entId: entId,
        sheetFormNo: [1070], // Зөв sheet form number ашиглах
        sheetCode: ['XM_11(1)_6'], // Хаалттай sheet code ашиглах
        accountantOid: params.accountantOid,
        reportNo: reportNo,
        year: params.year,
        period: params.taxPeriod
      });

      if (!saveSheetResult.success) {
        steps[2].status = 'failed';
        steps[2].error = saveSheetResult.error;
        onProgress?.(steps[2]);
        return {
          success: false,
          message: 'Sheet data хадгалахад алдаа гарлаа',
          error: saveSheetResult.error,
          details: saveSheetResult
        };
      }

      console.log('✅ Sheet data амжилттай хадгалагдлаа');
      console.log('🔍 Step 3 result дэлгэрэнгүй:');
      console.log('  - saveSheetResult:', saveSheetResult);
      console.log('  - saveSheetResult.data:', saveSheetResult.data);
      console.log('  - saveSheetResult.message:', saveSheetResult.message);
      console.log('  - successCount:', saveSheetResult.data?.successCount);
      console.log('  - totalSheets:', saveSheetResult.data?.totalSheets);
      
      steps[2].status = 'completed';
      steps[2].message = `Sheet data хадгалагдлаа: ${saveSheetResult.data?.successCount}/${saveSheetResult.data?.totalSheets}`;
      steps[2].data = saveSheetResult.data;
      onProgress?.(steps[2]);

    } catch (error: any) {
      console.error('❌ Sheet data хадгалахад алдаа:', error);
      steps[2].status = 'failed';
      steps[2].error = error.message;
      onProgress?.(steps[2]);
      return {
        success: false,
        message: 'Sheet data хадгалахад алдаа гарлаа',
        error: error.message
      };
    }

    // Step 4: Form data хадгалах (TaxSaveFormData) - дахин
    console.log('📝 Step 4: Form data хадгалах (дахин)...');
    steps[3].status = 'running';
    onProgress?.(steps[3]);

    console.log('🔍 Step 4 parameters дэлгэрэнгүй:');
    console.log('  - entId:', entId || '10913884');
    console.log('  - accountantOid:', params.accountantOid);
    console.log('  - formData:', params.formData);
    console.log('  - year:', params.year);
    console.log('  - period:', params.taxPeriod);

    try {
      const formDataResult2 = await saveFormData({
        entId: entId || '10913884', // Default entId from spreadsheet
        accountantOid: params.accountantOid,
        formData: params.formData,
        year: params.year,
        period: params.taxPeriod,
        taxTypeId: params.taxTypeId, // Pass taxTypeId from request
        formNo: params.formNo // Pass formNo from request
      });

      if (!formDataResult2.success) {
        steps[3].status = 'failed';
        steps[3].error = formDataResult2.error;
        onProgress?.(steps[3]);
        return {
          success: false,
          message: 'Form data хадгалахад алдаа гарлаа (дахин)',
          error: formDataResult2.error,
          details: formDataResult2
        };
      }

      // reportNo-г шинэчлэх
      reportNo = formDataResult2.data?.reportNo;
      entId = formDataResult2.data?.entId || entId;
      
      console.log('✅ Form data амжилттай хадгалагдлаа (дахин), reportNo:', reportNo);
      console.log('🔍 Step 4 result дэлгэрэнгүй:');
      console.log('  - formDataResult2:', formDataResult2);
      console.log('  - formDataResult2.data:', formDataResult2.data);
      console.log('  - formDataResult2.message:', formDataResult2.message);
      console.log('  - reportNo:', reportNo);
      console.log('  - entId:', entId);
      
      steps[3].status = 'completed';
      steps[3].message = `Form data хадгалагдлаа (дахин), reportNo: ${reportNo}`;
      steps[3].data = formDataResult2.data;
      onProgress?.(steps[3]);

    } catch (error: any) {
      console.error('❌ Form data хадгалахад алдаа (дахин):', error);
      steps[3].status = 'failed';
      steps[3].error = error.message;
      onProgress?.(steps[3]);
      return {
        success: false,
        message: 'Form data хадгалахад алдаа гарлаа (дахин)',
        error: error.message
      };
    }

    // Step 5: Report илгээх (TaxReportSubmit)
    console.log('📤 Step 5: Report илгээх...');
    steps[4].status = 'running';
    onProgress?.(steps[4]);

    console.log('🔍 Step 5 parameters дэлгэрэнгүй:');
    console.log('  - entId:', entId || '10913884');
    console.log('  - accountantOid:', params.accountantOid);
    console.log('  - submitData:', {
      year: params.year,
      period: params.taxPeriod,
      reportNo: reportNo
    });

    try {
      const submitResult = await submitReport({
        entId: entId || '10913884', // Зөв entId ашиглах
        accountantOid: params.accountantOid,
        submitData: {
          year: params.year,
          period: params.taxPeriod,
          reportNo: reportNo
        },
        taxTypeId: params.taxTypeId,
        formNo: params.formNo ? parseInt(params.formNo.toString()) : undefined,
        branchId: params.branchId,
        year: params.year,
        period: parseInt(params.taxPeriod.toString()),
        isXreport: params.isXreport
      });

      if (!submitResult.success) {
        steps[4].status = 'failed';
        steps[4].error = submitResult.error;
        onProgress?.(steps[4]);
        return {
          success: false,
          message: 'Report илгээхэд алдаа гарлаа',
          error: submitResult.error,
          details: submitResult
        };
      }

      console.log('✅ Report амжилттай илгээгдлээ');
      console.log('🔍 Step 5 result дэлгэрэнгүй:');
      console.log('  - submitResult:', submitResult);
      console.log('  - submitResult.data:', submitResult.data);
      console.log('  - submitResult.message:', submitResult.message);
      console.log('  - submitStatus:', submitResult.data?.submitStatus);
      
      steps[4].status = 'completed';
      steps[4].message = `Report илгээгдлээ: ${submitResult.data?.submitStatus}`;
      steps[4].data = submitResult.data;
      onProgress?.(steps[4]);

      // Бүх алхам амжилттай дууссан
      return {
        success: true,
        message: 'Tax HHOAT Report амжилттай илгээгдлээ',
        data: {
          steps: steps,
          finalResult: submitResult.data,
          summary: {
            reportNo: reportNo,
            entId: entId,
            year: params.year,
            taxPeriod: params.taxPeriod,
            submittedAt: new Date().toISOString()
          }
        }
      };

    } catch (error: any) {
      console.error('❌ Report илгээхэд алдаа:', error);
      steps[4].status = 'failed';
      steps[4].error = error.message;
      onProgress?.(steps[4]);
      return {
        success: false,
        message: 'Report илгээхэд алдаа гарлаа',
        error: error.message
      };
    }

  } catch (error: any) {
    console.error('🚨 Tax HHOAT Report submission алдаа:', error);
    return {
      success: false,
      message: 'Tax HHOAT Report илгээхэд алдаа гарлаа',
      error: error.message
    };
  }
};

/**
 * Step 1: Form data хадгалах API дуудах
 */
export const saveFormData = async (params: { entId: string; accountantOid: string; formData?: any; year?: number; period?: number; taxTypeId?: number; formNo?: string | number; record?: any }): Promise<TaxApiResponse> => {
  try {
    console.log('📝 Form data хадгалах API дуудаж байна...', params);
    
    // etaxToken шалгаж авах
    const etaxToken = await checkAndGetEtaxToken(params.accountantOid);
    if (!etaxToken) {
      return {
        success: false,
        message: 'etaxToken авч чадсангүй',
        error: 'Failed to get etaxToken'
      };
    }
    
    // Database-ээс мэдээлэл авах эсвэл өгөгдсөн formData ашиглах
    let requestData: any = {};
    
    if (params.formData && Object.keys(params.formData).length > 0) {
      // Өгөгдсөн formData ашиглах
      requestData = params.formData;
      console.log('📋 Өгөгдсөн formData ашиглаж байна');
    } else {
      // Database-ээс мэдээлэл авах эсвэл default formData үүсгэх
      requestData = await getFormDataFromDatabase(params.entId, params.accountantOid, params.year, params.period);
      console.log('📋 Database-ээс мэдээлэл аваад API format-тай болгож байна');
    }
    
    const response = await api.post('/api/tax/save-form-data', {
      entId: params.entId,
      accountantOid: params.accountantOid,
      formData: requestData,
      record: params.record, // Record data-г backend-д илгээх
      taxTypeId: params.taxTypeId, // Pass taxTypeId from selected row
      formNo: typeof params.formNo === 'string' ? parseInt(params.formNo) : params.formNo // Convert to number if string
    });

    if (response.success) {
      console.log('✅ Form data API амжилттай:', response.data);
      return response;
    } else {
      console.error('❌ Form data API алдаа:', response);
      return {
        success: false,
        message: response.message || 'Form data хадгалахад алдаа',
        error: response.error
      };
    }
  } catch (error: any) {
    console.error('❌ Form data API дуудахад алдаа:', error);
    return {
      success: false,
      message: 'Form data API дуудахад алдаа',
      error: error.message
    };
  }
};

/**
 * Step 2: Sheet data устгах API дуудах
 */
export const deleteAllSheetData = async (params: { 
  reportNo: string; 
  sheetFormNo: string[]; 
  accountantOid: string;
  entId?: string;
}): Promise<TaxApiResponse> => {
  try {
    console.log('🗑️ Sheet data устгах API дуудаж байна...', params);
    
    const response = await api.post('/api/tax/delete-all-sheet-data', {
      entId: params.entId,
      reportNo: params.reportNo,
      sheetFormNo: params.sheetFormNo,
      accountantOid: params.accountantOid
    });

    if (response.success) {
      console.log('✅ Sheet data устгах API амжилттай:', response.data);
      return response;
    } else {
      console.error('❌ Sheet data устгах API алдаа:', response);
      return {
        success: false,
        message: response.message || 'Sheet data устгахад алдаа',
        error: response.error
      };
    }
  } catch (error: any) {
    console.error('❌ Sheet data устгах API дуудахад алдаа:', error);
    return {
      success: false,
      message: 'Sheet data устгах API дуудахад алдаа',
      error: error.message
    };
  }
};

/**
 * Step 3: Sheet data хадгалах API дуудах
 */
export const saveSheetData = async (params: { 
  entId?: string; 
  sheetFormNo: number[]; 
  sheetCode: string[]; 
  accountantOid: string;
  reportNo?: string;
  year?: number;
  period?: number;
}): Promise<TaxApiResponse> => {
  try {
    console.log('💾 Sheet data хадгалах API дуудаж байна...', params);
    
    const response = await api.post('/api/tax/save-sheet-data', {
      entId: params.entId || '10913884', // Default entId
      sheetFormNo: params.sheetFormNo,
      sheetCode: params.sheetCode,
      accountantOid: params.accountantOid,
      reportNo: params.reportNo,
      year: params.year,
      period: params.period
    });

    if (response.success) {
      console.log('✅ Sheet data хадгалах API амжилттай:', response.data);
      return response;
    } else {
      console.error('❌ Sheet data хадгалах API алдаа:', response);
      return {
        success: false,
        message: response.message || 'Sheet data хадгалахад алдаа',
        error: response.error
      };
    }
  } catch (error: any) {
    console.error('❌ Sheet data хадгалах API дуудахад алдаа:', error);
    return {
      success: false,
      message: 'Sheet data хадгалах API дуудахад алдаа',
      error: error.message
    };
  }
};

/**
 * Step 4: Report илгээх API дуудах
 */
export const submitReport = async (params: { 
  entId: string; 
  accountantOid: string; 
  submitData?: any;
  taxTypeId?: number;
  formNo?: number;
  branchId?: number;
  year?: number;
  period?: number;
  isXreport?: number;
}): Promise<TaxApiResponse> => {
  try {
    console.log('📤 Report илгээх API дуудаж байна...', params);
    
    const response = await api.post('/api/tax/report-submit', {
      entId: params.entId,
      accountantOid: params.accountantOid,
      submitData: params.submitData,
      taxTypeId: params.taxTypeId,
      formNo: params.formNo,
      branchId: params.branchId,
      year: params.year,
      period: params.period,
      isXreport: params.isXreport
    });

    if (response.success) {
      console.log('✅ Report илгээх API амжилттай:', response.data);
      return response;
    } else {
      console.error('❌ Report илгээх API алдаа:', response);
      return {
        success: false,
        message: response.message || 'Report илгээхэд алдаа',
        error: response.error
      };
    }
  } catch (error: any) {
    console.error('❌ Report илгээх API дуудахад алдаа:', error);
    return {
      success: false,
      message: 'Report илгээх API дуудахад алдаа',
      error: error.message
    };
  }
};

/**
 * CustomerOid-аар тайлан илгээх
 * @param params - Тайлан илгээх параметрүүд
 */
export const submitReportByCustomerOid = async (params: { 
  customerOid: string;
  submitData?: any;
  taxTypeId?: number;
  formNo?: number;
  branchId?: number;
  year?: number;
  period?: number;
  isXreport?: number;
  reportNo?: number; // reportNo-г шууд параметр болгон нэмсэн
}): Promise<TaxApiResponse> => {
  try {
    console.log('📤 Report илгээх API (CustomerOid-аар) дуудаж байна...', params);
    
    // reportNo-г submitData-аас эсвэл шууд параметрээс авна
    const reportNo = params.reportNo || params.submitData?.reportNo;
    
    const endpoint = `/api/tax/report-submit/by-customer?customerOid=${params.customerOid}`;
    const requestBody = {
      reportNo: reportNo, // reportNo-г шууд body-д оруулна (backend-ийн шаардлага)
      submitData: params.submitData,
      taxTypeId: params.taxTypeId,
      formNo: params.formNo,
      branchId: params.branchId,
      year: params.year,
      period: params.period,
      isXreport: params.isXreport
    };
    
    console.log('🔍 API Request details:', {
      endpoint: endpoint,
      method: 'POST',
      body: requestBody,
      customerOid: params.customerOid
    });
    
    const response = await api.post(endpoint, requestBody);
    
    console.log('🔍 API Response details:', {
      success: response.success,
      message: response.message,
      data: response.data,
      error: response.error
    });

    if (response && response.success) {
      console.log('✅ Report илгээх API (CustomerOid-аар) амжилттай:', response.data);
      return response;
    } else {
      console.error('❌ Report илгээх API (CustomerOid-аар) алдаа:', response);
      // Backend-ээс ирж буй message-ийг шууд ашиглах (Etax API-аас ирж буй message байж болно)
      return {
        success: false,
        message: response?.message || 'Report илгээхэд алдаа',
        error: response?.error || 'Unknown error'
      };
    }
  } catch (error: any) {
    console.error('❌ Report илгээх API (CustomerOid-аар) дуудахад алдаа:', error);
    console.error('❌ Error details:', {
      message: error.message,
      stack: error.stack,
      response: error.response,
      status: error.status,
      statusText: error.statusText,
      responseData: error.response?.data
    });
    
    // Backend-ээс ирж буй error response-ийг шалгах (Etax API-аас ирж буй message байж болно)
    const errorMessage = error.response?.data?.message || error.message || 'Report илгээх API дуудахад алдаа';
    
    return {
      success: false,
      message: errorMessage, // Backend-ээс ирж буй message-ийг ашиглах (Etax API-аас ирж буй message байж болно)
      error: error.response?.data?.error || error.message || 'Unknown error',
      details: error.response?.data || error.response || error
    };
  }
};

/**
 * Улирлын дугаарыг тоо болгон хувиргах helper функц
 * "3-р улирал" -> 3
 */
export const parseQuarter = (quarterText: string): number => {
  const match = quarterText.match(/(\d+)/);
  return match ? parseInt(match[1]) : 1;
};

/**
 * Жилийн мэдээллийг шалгах helper функц
 */
export const validateYear = (year: number): boolean => {
  const currentYear = new Date().getFullYear();
  return year >= 2020 && year <= currentYear + 1;
};

/**
 * etaxToken-г localStorage дээрээс шалгах функц
 */
const checkAndGetEtaxToken = async (accountantOid: string): Promise<string | null> => {
  try {
    // localStorage дээрээс etaxToken шалгах
    const storedToken = localStorage.getItem('etax_token');
    const storedExpiry = localStorage.getItem('etax_token_expiry');
    
    if (storedToken && storedExpiry) {
      const expiryTime = new Date(storedExpiry);
      const now = new Date();
      const bufferTime = 5 * 60 * 1000; // 5 минут buffer
      
      if (expiryTime.getTime() - now.getTime() > bufferTime) {
        console.log('✅ localStorage дээрээс хүчинтэй etaxToken олдлоо');
        return storedToken;
      } else {
        console.log('⚠️ localStorage дээрээс etaxToken хугацаа дууссан');
        localStorage.removeItem('etax_token');
        localStorage.removeItem('etax_token_expiry');
      }
    }
    
    console.log('🔄 localStorage дээр etaxToken байхгүй, backend-ээс аваж байна...');
    
    // Backend-ээс шинэ etaxToken авах
    const tokenResponse = await api.post('/api/taxsystem/get-etax-token', {
      accountantOid: accountantOid
    });
    
    if (tokenResponse.success && tokenResponse.data?.access_token) {
      const accessToken = tokenResponse.data.access_token;
      const expiry = tokenResponse.data.expiry;
      
      // localStorage-д хадгалах
      localStorage.setItem('etax_token', accessToken);
      localStorage.setItem('etax_token_expiry', expiry);
      
      console.log('✅ Шинэ etaxToken амжилттай авч localStorage-д хадгалав');
      return accessToken;
    } else {
      console.error('❌ Backend-ээс etaxToken авч чадсангүй');
      return null;
    }
  } catch (error) {
    console.error('❌ etaxToken шалгах/авах алдаа:', error);
    return null;
  }
};

/**
 * Database-ээс TaxReportHHOAT мэдээлэл авах функц
 */
export const getFormDataFromDatabase = async (entId: string, accountantOid: string, year?: number, period?: number): Promise<any> => {
  try {
    console.log('🗃️ Database-ээс TaxReportHHOAT мэдээлэл авах...');
    console.log('📋 getFormDataFromDatabase параметрүүд:');
    console.log('  - entId:', entId);
    console.log('  - accountantOid:', accountantOid);
    console.log('  - year parameter:', year);
    console.log('  - period parameter:', period);
    
    // Database-ээс мэдээлэл авах API дуудах
    const response = await api.get(`/api/tax-post-hhoat/salary-sheet?entId=${entId}&accountantOid=${accountantOid}`);
    
    if (response.success && response.data) {
      console.log('✅ Database-ээс мэдээлэл амжилттай авлаа:', response.data.length, 'records');
      
      // Database-ээс ирсэн мэдээллийг API format-тай болгох - backend-ээс авсан утгуудыг ашиглах
      const reportDataDetail = response.data.map((row: any, index: number) => ({
        tagId: parseInt(row.TagId), // Хэрэв TagId байхгүй бол index + 1 ашиглах
        tagKey: row.TagKey, // Etax API-д tagKey шаардлагатай
        value: (row.Amount || 0).toString(),
        toolTipOpen: false, // Backend-ээс ирэхгүй бол default утга
        errorMsgs: '', // Backend-ээс ирэхгүй бол default утга
        errType: '', // Backend-ээс ирэхгүй бол default утга
        type: 1, // Backend-ээс ирэхгүй бол default утга
        isValid: 1 // Backend-ээс ирэхгүй бол default утга
      }));
      
      // Amount утгаас хамааруулан isXreport тохируулах
      const hasNonZeroAmount = reportDataDetail.some(item => {
        const amount = parseFloat(item.value) || 0;
        return amount > 0;
      });
      
      // isXreport логик: Backend-д тохируулагдана, энд зөвхөн Amount утгаас хамааруулан тохируулах
      const isXreport = hasNonZeroAmount ? 0 : 1; // Хүснэгтэд мэдээлэл байгаа эсэхээс хамаарч тохируулах
      console.log('🔍 TaxSendHHOAT Amount утга шалгаж байна:', {
        totalRecords: reportDataDetail.length,
        hasNonZeroAmount: hasNonZeroAmount,
        isXreport: isXreport,
        sampleValues: reportDataDetail.slice(0, 3).map(item => ({ value: item.value, amount: parseFloat(item.value) || 0 }))
      });
      
      // Default утгууд ашиглах хэрэв year эсвэл period өгөгдөөгүй бол
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const defaultYear = year || currentYear;
      const defaultPeriod = period || Math.ceil((currentDate.getMonth() + 1) / 3);
      
      console.log('📋 Year параметр тооцоолол:');
      console.log('  - year parameter:', year);
      console.log('  - currentYear:', currentYear);
      console.log('  - defaultYear (year || currentYear):', defaultYear);
      console.log('  - Source:', year ? 'year parameter' : 'currentYear (default)');
      
      const formData = {
        reportData: {
          year: defaultYear,
          period: defaultPeriod
        },
        reportDataDetail: reportDataDetail
      };
      
      console.log('📋 API format-тай болгосон formData:', formData.reportDataDetail.length, 'records');
      return formData;
    } else {
      console.log('⚠️ Database-ээс мэдээлэл олдсонгүй, хоосон reportDataDetail-тай formData үүсгэж байна');
      
      // Default утгууд ашиглах хэрэв year эсвэл period өгөгдөөгүй бол
      const currentDate = new Date();
      const defaultYear = year || currentDate.getFullYear();
      const defaultPeriod = period || Math.ceil((currentDate.getMonth() + 1) / 3);
      
      // Хоосон formData үүсгэх - backend-д database-аас бүх утгуудыг авна
      const formData = {
        reportData: {
          year: defaultYear,
          period: defaultPeriod
        },
        reportDataDetail: [] // Хоосон массив - backend-д database-аас авна
      };
      
      console.log('📋 Хоосон reportDataDetail-тай formData үүсгэсэн');
      return formData;
    }
  } catch (error: any) {
    console.error('❌ Database-ээс мэдээлэл авахад алдаа:', error);
    
    // Алдаа гарсан тохиолдолд хоосон reportDataDetail-тай formData буцаах
    console.log('⚠️ Алдаа гарсан тохиолдолд хоосон reportDataDetail-тай formData буцаах');
    
    // Default утгууд ашиглах хэрэв year эсвэл period өгөгдөөгүй бол
    const currentDate = new Date();
    const defaultYear = year || currentDate.getFullYear();
    const defaultPeriod = period || Math.ceil((currentDate.getMonth() + 1) / 3);
    
    const errorFormData = {
      reportData: {
        year: defaultYear,
        period: defaultPeriod
      },
      reportDataDetail: [] // Хоосон массив - backend-д database-аас авна
    };
    
    return errorFormData;
  }
};

/**
 * Tax HHOAT Report submission-г эхлүүлэх wrapper функц
 * Frontend component-ээс дуудахад ашиглана
 */
export const handleTaxReportSubmission = async (
  year: number,
  quarterText: string,
  accountantOid: string,
  entId?: string,
  formData?: any,
  onProgress?: (step: TaxSubmissionStep) => void,
  onSuccess?: (result: TaxApiResponse) => void,
  onError?: (error: TaxApiResponse) => void,
  taxTypeId?: number,
  formNo?: number
): Promise<void> => {
  try {
    console.log('🚀 Tax HHOAT Report submission эхэлж байна...', {
      year,
      quarterText,
      accountantOid,
      entId
    });

    // Жилийн мэдээллийг шалгах
    if (!validateYear(year)) {
      const error = {
        success: false,
        message: 'Жилийн мэдээлэл буруу байна',
        error: `Жил: ${year} - зөвхөн 2020-${new Date().getFullYear() + 1} хооронд байх ёстой`
      };
      onError?.(error);
      return;
    }

    // Улирлын дугаарыг парс хийх
    const taxPeriod = parseQuarter(quarterText);
    if (taxPeriod < 1 || taxPeriod > 4) {
      const error = {
        success: false,
        message: 'Улирлын мэдээлэл буруу байна',
        error: `Улирал: ${quarterText} - 1-4 улирал байх ёстой`
      };
      onError?.(error);
      return;
    }

    // AccountantOid шалгах
    if (!accountantOid) {
      const error = {
        success: false,
        message: 'Нягтлан бодогчийн мэдээлэл байхгүй',
        error: 'AccountantOid шаардлагатай'
      };
      onError?.(error);
      return;
    }

    // Tax HHOAT Report submission эхлүүлэх
    const result = await submitTaxHHOATReport({
      year,
      taxPeriod,
      entId,
      accountantOid,
      formData,
      taxTypeId,
      formNo: formNo ? formNo.toString() : undefined,
      branchId: undefined, // Will be fetched from database
      isXreport: undefined // Will be calculated based on formNo
    }, onProgress);

    if (result.success) {
      console.log('✅ Tax HHOAT Report submission амжилттай дууслаа:', result);
      onSuccess?.(result);
    } else {
      console.error('❌ Tax HHOAT Report submission алдаатай дууслаа:', result);
      onError?.(result);
    }

  } catch (error: any) {
    console.error('🚨 Tax HHOAT Report submission wrapper алдаа:', error);
    const errorResponse = {
      success: false,
      message: 'Tax HHOAT Report submission wrapper алдаа',
      error: error.message
    };
    onError?.(errorResponse);
  }
};
